// Reac Component
import React from 'react'
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from 'react-i18next'

// App Component
import { FamlaServices, i18n } from '../../services'
import { Buttons, InputForm } from '../../components'
import { Routes } from '../../routes'


// Features

// import ButtonSocialeNetwork from './ButtonSocialeNetwork'
import { Metrics } from '../../constants'
import { isPrivateEmail } from '../../utils/emailUtils'
import { isStCorporateEmail, shouldRedirectToStPortal } from '../../utils/stCorporateRedirect'
import StRedirectNotice from './StRedirectNotice'

//interface
import { iAlert } from '../../interfaces'
import { company, login, useAppDispatch } from '../../stores'
import iSessions from './Sessions'
import { iUser } from '../../components/type'
import './UsersLogin.css'

const UsersReferral = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [compagny, setCompagny] = React.useState({
        name: '',
        email: '',
        industry: '',
        password: '',
        referral: '',
        isSubmit: false,
        isValidMail: true
    })
    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })
    /** Adresse corporate à orienter vers le portail SSO (affiche l'interstitiel). */
    const [stRedirectEmail, setStRedirectEmail] = React.useState<string | null>(null)

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();


        if (compagny.email.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.emailIsRequired') });
            return;
        }
        else if (compagny.name.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.compagnyNameIsRequired') });
            return;
        }

        // Vérifier si l'email est professionnel avant de soumettre
        if (isPrivateEmail(compagny.email)) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.professionalEmail') });
            return;
        }

        // [ST REDIRECT] Saisie corporate sur l'instance publique → interstitiel puis portail SSO.
        if (shouldRedirectToStPortal() && isStCorporateEmail(compagny.email)) {
            setStRedirectEmail(compagny.email.trim());
            setCompagny({ ...compagny, isSubmit: false });
            return;
        }

        // Détecter la langue actuelle de l'application
        // Priorité: i18n.language > localStorage > navigator.language
        let currentLang = i18n.language;
        if (!currentLang || currentLang === 'undefined') {
            // Si i18n n'est pas encore initialisé, utiliser localStorage
            const savedLang = localStorage.getItem('currentLanguage');
            if (savedLang) {
                currentLang = savedLang;
            } else {
                // Sinon, détecter depuis le navigateur
                const browserLang = navigator.language || navigator.languages?.[0] || 'en';
                currentLang = browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
            }
        }
        // Normaliser la langue : convertir 'fr-FR', 'fr-CA', etc. en 'fr'
        const normalizedLang = currentLang?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
        const backendLang = normalizedLang === 'fr' ? 'french' : 'english';
        
        let formData = {
            fullname: compagny.name,
            email: compagny.email,
            password: compagny.password,
            passwordConfirm: compagny.password,
            referral: compagny.referral,
            lang: backendLang
        }
        setCompagny({ ...compagny, isSubmit: true });
        let data = await FamlaServices("api_host", "/auth/register", "POST", formData, "")

        // Filet de sécurité : l'API refuse les adresses corporate sur l'instance publique.
        if (data.status === 403 && data.body?.data?.sso_portal_url) {
            setCompagny({ ...compagny, isSubmit: false });
            setStRedirectEmail(compagny.email.trim());
            return;
        }

        if (data.status === 201) {
            let session = {} as iSessions;
            let user: iUser = data.body.data
            dispatch(login({
                token: "",
                session: session,
                user: user
            }));

            setCompagny({ ...compagny, name: '', email: '', password: '', referral: '', isSubmit: false, isValidMail: true });
            navigate(Routes.AUTH_MAIL);
        }
        else if (data.body.status_code === 409) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.accountAlreadyExist') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && data.body.description === "This email address has been banned from Famla") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.thisEmailHasBanned') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && data.body.description === "Passwords do not match") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.passwordsNotMatch') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else if (data.body.status_code === 400 && data.body.description === "dont use private") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.professionalEmail') });
            setCompagny({ ...compagny, password: '', isSubmit: false });
        }
        else {
            setCompagny({ ...compagny, password: '', isSubmit: false });
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.anErrorOcured') });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { type, name, value, checked } = event.target;
        let fieldValue: string | number | boolean;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else if (type === 'number') {
            fieldValue = value !== '' ? Number(value) : '';
        } else {
            fieldValue = value !== undefined ? value : '';
        }
        fieldValue = typeof fieldValue !== 'string' ? String(fieldValue) : fieldValue;
        const trimmedValue: string = typeof fieldValue === 'string' ? fieldValue.trim() : '';
        let isEmailValid = name === 'email' ? Metrics.mailFormat.test(trimmedValue) : true;

        // Réinitialiser l'alerte quand l'utilisateur modifie l'email
        if (name === 'email' && alert.isActive) {
            setAlert({ isActive: false, type: '', message: '' });
        }

        setCompagny({ ...compagny, [name]: fieldValue, isValidMail: isEmailValid });
    };

    return (
        <div className="users-login-light">
            {stRedirectEmail && (
                <StRedirectNotice
                    email={stRedirectEmail}
                    onCancel={() => setStRedirectEmail(null)}
                />
            )}
            <div className="users-login-title-block">
                <h1 className="users-login-headline">{t('views.auth.signUp')}</h1>
            </div>
            <form>
                {alert.isActive &&
                    <div className={`alert alert-${alert.type}`} role="alert">
                        {alert.message}
                    </div>
                }

                <InputForm
                    inputType="email"
                    inputLabel={t('views.auth.emailAddresPro')}
                    inputName="email"
                    inputValue={compagny.email}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={compagny.isValidMail ? 'mb-3' : 'mb-0'}
                />
                {!(compagny.isValidMail) && <span className={`text-danger fs-6 d-bloc ${!compagny.isValidMail ? 'mb-3' : 'mb-0'}`}>{compagny.isValidMail ? '' : t('views.auth.enterValidMail')}</span>}

                <InputForm
                    inputType="text"
                    inputLabel={t('words.name')}
                    inputName="name"
                    inputValue={compagny.name}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={compagny.isValidMail ? 'mb-3' : 'mb-0'}
                />
                {/* <SelectForm
                    formClass="mb-3"
                <SelectForm
                    selectClass="mb-3"
                    placeholder="Select Industry"
                    selectName={compagny.industry}
                    selectOptions={selectOptions}
                    onChange={(e) => handleChange(e)}
                /> */}
                <InputForm
                    inputType="text"
                    inputLabel={t('views.auth.referral')}
                    inputName="referral"
                    inputValue={compagny.referral}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass="mb-3"
                />
                <InputForm
                    inputType="password"
                    inputLabel={t('views.auth.password')}
                    inputName="password"
                    inputValue={compagny.password}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={compagny.isValidMail ? 'mb-3 ' : 'mt-2'}
                    withIcon={true}
                    iconClass="text-theme"
                />

                <Buttons
                    buttonType="submit"
                    name={t('views.auth.signUp')}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleSubmit(event)}
                    isLoading={compagny.isSubmit}
                    btnClass="users-login-submit w-100"
                    labelClass="text-white fw-bold"
                    styleProps={{ minHeight: 48 }}
                    arrowVisible={false}
                    spinnerColor="text-white"
                    arrowColor=""
                    disabled={compagny.password.length === 0 || compagny.email.length === 0 || compagny.name.length === 0}
                />
            </form>

            <p className="users-login-signup-footer mb-0">
                {t('views.auth.alreadyHaveAccount')}{' '}
                <Link to={Routes.AUTH_LOGIN} className="users-login-terracotta-link fw-medium">
                    {t('views.auth.logIn')}
                </Link>
            </p>

            {/* <ButtonSocialeNetwork t={t} /> */}
        </div>
    )
}

export default UsersReferral;

